import * as Y from 'yjs';

import type { AgentOperationUpdateProgress } from '@canvas/protocol-types';

/**
 * Identifies a backend agent operation and carries either one received Yjs
 * update or the completed operation's merged Yjs update.
 */
type BackendAgentOperationUpdate = {
  operationId: string;
  agentOwnerAccountId?: string;
  update: Uint8Array;
};

/**
 * Transient reassembly state for one backend agent operation while its
 * indexed Yjs updates are still arriving.
 */
type PendingBackendAgentOperation = {
  agentOwnerAccountId?: string;
  // Keyed by index to prevent duplicate delivery of updates from increasing
  // completion progress
  updatesByIndex: Map<number, Uint8Array>;
  updateCount?: number;
};

export class BackendAgentOperationBuffer {
  /**
   * Map of operation IDs to their transient reassembly state.
   */
  private readonly pendingOperations = new Map<string, PendingBackendAgentOperation>();

  addUpdate({
    operationId,
    agentOperationUpdateIndex,
    agentOperationUpdateCount,
    agentOwnerAccountId,
    update,
  }: BackendAgentOperationUpdate & AgentOperationUpdateProgress):
    | BackendAgentOperationUpdate
    | undefined {
    let operation = this.pendingOperations.get(operationId);
    if (operation === undefined) {
      operation = {
        agentOwnerAccountId,
        updatesByIndex: new Map(),
      };
      this.pendingOperations.set(operationId, operation);
    }

    // updateCount is only present on the first update in an operation
    if (agentOperationUpdateCount !== undefined) {
      operation.updateCount = agentOperationUpdateCount;
    }

    if (!operation.updatesByIndex.has(agentOperationUpdateIndex)) {
      operation.updatesByIndex.set(agentOperationUpdateIndex, update);
    }

    // Return early to signify incomplete operation. We don't need to check
    // every index because the backend producer guarantees every index is within
    // [0, updateCount) by virtue of it using the .map() index parameter.
    if (
      operation.updateCount === undefined ||
      operation.updatesByIndex.size !== operation.updateCount
    ) {
      return undefined;
    }

    // At this point, all updates have been received and the operation is
    // complete - we can reconstruct the updates as a single merged update.
    // Note: Yjs updates are commutative so ordering doesn't matter.
    const mergedUpdate = Y.mergeUpdates([...operation.updatesByIndex.values()]);
    this.pendingOperations.delete(operationId);

    return {
      operationId,
      agentOwnerAccountId: operation.agentOwnerAccountId,
      update: mergedUpdate,
    };
  }

  clear(): void {
    this.pendingOperations.clear();
  }
}
